import { saveAs } from "file-saver";
import {
  ChevronRightIcon,
  CopyIcon,
  DownloadIcon,
  MoreHorizontalIcon,
} from "lucide-react";

import { AudioPlayer } from "@/components/AudioPlayer";
import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useSuccessToast } from "@/hooks/useSuccessToast";
import { misc } from "@/lib/misc";
import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { transcript } from "@/lib/transcript";

export const CallWithTranscript = ({
  audioRef,
  audioSrc,
  defaultOpen = false,
  downloadFilename = "call-audio.mp3",
  getRoleLabel,
  transcriptLines,
}: {
  audioRef: React.RefObject<HTMLAudioElement>;
  audioSrc: string;
  defaultOpen?: boolean;
  downloadFilename?: string;
  getRoleLabel: (role: string) => string;
  transcriptLines: {
    content: string;
    role: string;
    time: string;
  }[];
}) => {
  const successToast = useSuccessToast();

  return (
    <Collapsible className="group/collapsible" defaultOpen={defaultOpen}>
      <Column className="gap-4 rounded-lg border border-slate-200 p-6">
        <Row className="items-center justify-between">
          <CollapsibleTrigger asChild>
            <Button
              onClick={() => {
                mixpanelUtils.trackButtonClick(
                  "Transcript open/close toggle",
                  getScreenFromPathname(location.pathname),
                );
              }}
              size="sm"
              variant="ghost"
            >
              <ChevronRightIcon className="h-4 w-4 text-slate-700 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
              <span className="text-sm text-slate-700">
                <span className="group-data-[state=closed]/collapsible:inline group-data-[state=open]/collapsible:hidden">
                  View transcript
                </span>
                <span className="group-data-[state=open]/collapsible:inline group-data-[state=closed]/collapsible:hidden">
                  Hide transcript
                </span>
              </span>
            </Button>
          </CollapsibleTrigger>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost">
                <MoreHorizontalIcon className="h-4 w-4 text-slate-700" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  const text = misc.buildClipboardTextForTranscript({
                    getRoleLabel,
                    transcriptLines,
                  });
                  void navigator.clipboard.writeText(text);
                  successToast({
                    description: "Copied transcript to clipboard!",
                  });
                }}
              >
                <CopyIcon className="h-4 w-4" />
                Copy full transcript
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  saveAs(audioSrc, downloadFilename);
                }}
              >
                <DownloadIcon className="h-4 w-4" />
                Download audio
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Row>
        <AudioPlayer audioRef={audioRef} audioSrc={audioSrc} />
        <CollapsibleContent>
          <Column className="gap-2">
            {transcriptLines.map((line, i) => (
              <div
                className="grid grid-cols-[40px_1fr] items-start gap-6"
                key={i}
              >
                <Button
                  className="h-auto p-0 text-sm font-medium text-primary-900"
                  onClick={() => {
                    if (!audioRef.current) {
                      return;
                    }

                    const timeInSeconds = transcript.convertTimestampToSeconds(
                      line.time,
                    );
                    if (timeInSeconds === null) {
                      return;
                    }

                    mixpanelUtils.trackButtonClick(
                      "Seek audio from transcript timestamp",
                      getScreenFromPathname(location.pathname),
                    );
                    audioRef.current.currentTime = timeInSeconds;
                    void audioRef.current.play();
                  }}
                  variant="link"
                >
                  {line.time}
                </Button>
                <p className="text-sm text-slate-700">
                  <span className="font-medium text-slate-800">
                    {getRoleLabel(line.role)}:{" "}
                  </span>
                  {line.content}
                </p>
              </div>
            ))}
          </Column>
        </CollapsibleContent>
      </Column>
    </Collapsible>
  );
};
